import { RouteRecordRaw } from 'vue-router';

import { ROUTE_NAMES } from '@/constants';
import { env } from '@/constants/env';
import { RouteTab } from '@/models/routing';
import i18n, { loadTranslations } from '@/plugins/i18n';

import { authenticationGuard, fetchCommonData } from './guards';

const ReplenishmentView = () => import('@/pages/Replenishment/index.vue');
const ReplenishmentInboundOrders = () => import('@/pages/Replenishment/InboundOrders/ReplenishmentInboundOrders.vue');
const ReplenishmentInventory = () => import('@/pages/Replenishment/Inventory/ReplenishmentInventory.vue');
const ReplenishmentDraftOrders = () => import('@/pages/Replenishment/Orders/ReplenishmentDraftOrders.vue');
const ReplenishmentDraftOrderEdit = () => import('@/pages/Replenishment/Orders/ReplenishmentDraftOrderEdit.vue');
const ReplenishmentOrders = () => import('@/pages/Replenishment/Orders/ReplenishmentOrders.vue');
const ReplenishmentSuggestions = () => import('@/pages/Replenishment/Suggestions/ReplenishmentSuggestions.vue');
const ReplenishmentCatalog = () => import('@/pages/Replenishment/Catalog/ReplenishmentCatalog.vue');
const InboundShipmentsCreate = () => import('@/pages/Replenishment/InboundShipmentsCreate/InboundShipmentsCreate.vue');
const InboundShipmentSummary = () => import('@/pages/Replenishment/InboundShipmentSummary/InboundShipmentSummary.vue');

export function replenishmentTabRoutes(): Readonly<RouteTab[]> {
  const routes = [
    {
      to: { name: ROUTE_NAMES.REPLENISHMENT_INVENTORY },
      label: i18n.t('replenishment.inventory.tab'),
      visible: ({ userPermissions }) => {
        return userPermissions?.canAccessReplenishment;
      },
    },
    {
      to: { name: ROUTE_NAMES.REPLENISHMENT_ORDERS },
      label: i18n.t('replenishment.title'),
      visible: ({ userPermissions }) => {
        return userPermissions?.canAccessReplenishment;
      },
    },
  ];

  if (env.VITE_REPLENISHMENT_DRAFTS) {
    routes.push({
      to: { name: ROUTE_NAMES.REPLENISHMENT_DRAFT_ORDERS },
      label: i18n.t('replenishment.orders.drafts.tab'),
      visible: ({ userPermissions }) => {
        return userPermissions?.canAccessReplenishment;
      },
    });
  }

  if (env.VITE_REPLENISHMENT_SUGGESTIONS) {
    routes.push({
      to: { name: ROUTE_NAMES.REPLENISHMENT_SUGGESTIONS },
      label: i18n.t('replenishment.suggestions.tab'),
      visible: ({ userPermissions }) => {
        return userPermissions?.canAccessReplenishment;
      },
    });
  }

  routes.push({
    to: { name: ROUTE_NAMES.REPLENISHMENT_INBOUND_ORDERS },
    label: i18n.t('replenishment.inboundOrders.tab'),
    visible: ({ userPermissions }) => {
      return userPermissions?.canAccessReplenishment;
    },
  });

  return Object.freeze(routes);
}

async function fetchTranslations() {
  await loadTranslations('replenishment');
}

const replenishmentRoutes: RouteRecordRaw[] = [
  {
    path: '/replenishment/',
    component: ReplenishmentView,
    beforeEnter: [authenticationGuard, fetchCommonData, fetchTranslations],
    meta: {
      tabs: replenishmentTabRoutes,
    },
    children: [
      {
        path: '',
        redirect: { name: ROUTE_NAMES.REPLENISHMENT_INVENTORY },
      },
      {
        path: 'inventory',
        name: ROUTE_NAMES.REPLENISHMENT_INVENTORY,
        component: ReplenishmentInventory,
        meta: {
          title: () => i18n.t('replenishment.inventory.title'),
          description: () => i18n.t('replenishment.inventory.description'),
          permissions: [
            {
              rules: ['canAccessReplenishment', ({ userActiveCompany }) => Boolean(userActiveCompany?.staffId)],
            },
          ],
        },
      },
      {
        path: 'inbound-shipments',
        name: ROUTE_NAMES.REPLENISHMENT_INBOUND_ORDERS,
        component: ReplenishmentInboundOrders,
        meta: {
          title: () => i18n.t('replenishment.inboundOrders.title'),
          description: () => i18n.t('replenishment.inboundOrders.description'),
          permissions: [
            {
              rules: ['canAccessReplenishment', ({ userActiveCompany }) => Boolean(userActiveCompany?.staffId)],
            },
          ],
        },
      },
      {
        path: 'inbound-shipments-create',
        name: ROUTE_NAMES.REPLENISHMENT_INBOUND_SHIPMENTS_CREATE,
        component: InboundShipmentsCreate,
        meta: {
          title: () => i18n.t('replenishment.inboundShipmentsCreate.title'),
          description: () => i18n.t('replenishment.inboundShipmentsCreate.description'),
          permissions: [
            {
              rules: [
                'canAccessReplenishment',
                'inbound_shipments_create',
                ({ userActiveCompany }) => Boolean(userActiveCompany?.staffId),
              ],
            },
          ],
        },
      },
      {
        path: 'inbound-shipments/:uuid/summary',
        name: ROUTE_NAMES.REPLENISHMENT_INBOUND_SHIPMENT_SUMMARY,
        component: InboundShipmentSummary,
        props: (route) => {
          return { uuid: route.params.uuid };
        },
        meta: {
          title: () => i18n.t('replenishment.inboundShipmentSummary.self'),
          permissions: [
            {
              rules: ['canAccessReplenishment', ({ userActiveCompany }) => Boolean(userActiveCompany?.staffId)],
            },
          ],
        },
      },
      {
        path: 'orders',
        name: ROUTE_NAMES.REPLENISHMENT_ORDERS,
        component: ReplenishmentOrders,
        meta: {
          title: () => i18n.t('replenishment.orders.title'),
          description: () => i18n.t('replenishment.orders.description'),
          permissions: [
            {
              rules: ['canAccessReplenishment', ({ userActiveCompany }) => Boolean(userActiveCompany?.staffId)],
            },
          ],
        },
      },
      {
        path: 'drafts',
        name: ROUTE_NAMES.REPLENISHMENT_DRAFT_ORDERS,
        component: ReplenishmentDraftOrders,
        meta: {
          title: () => i18n.t('replenishment.orders.drafts.title'),
          description: () => i18n.t('replenishment.orders.drafts.description'),
          permissions: [
            {
              rules: ['canAccessReplenishment', ({ userActiveCompany }) => Boolean(userActiveCompany?.staffId)],
            },
          ],
        },
      },
      {
        path: 'suggestions',
        name: ROUTE_NAMES.REPLENISHMENT_SUGGESTIONS,
        component: ReplenishmentSuggestions,
        meta: {
          title: () => i18n.t('replenishment.suggestions.title'),
          description: () => i18n.t('replenishment.suggestions.description'),
          permissions: [
            {
              rules: ['canAccessReplenishment', ({ userActiveCompany }) => Boolean(userActiveCompany?.staffId)],
            },
          ],
        },
      },
      {
        path: 'catalog',
        name: ROUTE_NAMES.REPLENISHMENT_CATALOG,
        component: ReplenishmentCatalog,
        meta: {
          title: () => i18n.t('replenishment.catalog.title'),
          description: () => i18n.t('replenishment.catalog.description'),
          permissions: [
            {
              rules: [
                'canAccessReplenishment',
                'replenishment_catalog_page',
                ({ userActiveCompany }) => Boolean(userActiveCompany?.staffId),
              ],
            },
          ],
        },
      },
    ],
  },
  {
    path: '/replenishment/drafts/:draftOrderId/edit',
    name: ROUTE_NAMES.REPLENISHMENT_DRAFT_EDIT,
    props: true,
    beforeEnter: [authenticationGuard, fetchCommonData, fetchTranslations],
    component: ReplenishmentDraftOrderEdit,
    meta: {
      permissions: [
        {
          rules: ['canAccessReplenishment', ({ userActiveCompany }) => Boolean(userActiveCompany?.staffId)],
        },
      ],
    },
  },
];

export default replenishmentRoutes;
